import React, {useEffect, useState} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {App, Button, Card, Form, Input, Steps, Typography} from 'antd';
import {CheckCircleOutlined, GiftOutlined, LockOutlined, MailOutlined, UserOutlined} from '@ant-design/icons';
import {useI18n} from '../../i18n/useI18n';
import LanguageSwitcher from '../../components/site/LanguageSwitcher';
import {buildLocalizedSitePath, isSiteLocale} from '../../i18n/siteLocalePath';
import {useAuth} from '../../contexts/AuthContext';
import {invitationCodeService} from '../../services/invitationCodeService';
import styles from './AuthPages.module.css';

const {Text} = Typography;

interface RegisterFormValues {
    username: string;
    password: string;
    confirmPassword: string;
    email: string;
}

const RegisterPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const {register, isLoading} = useAuth();
    const {locale, setLocale, t} = useI18n();
    const {siteLocale} = useParams();

    // 从URL路径同步 locale（支持 /zh-CN/register, /en-US/register 等）
    useEffect(() => {
        if (siteLocale && isSiteLocale(siteLocale) && siteLocale !== locale) {
            setLocale(siteLocale);
        }
    }, [locale, setLocale, siteLocale]);

    const [form] = Form.useForm<RegisterFormValues>();
    const [currentStep, setCurrentStep] = useState(0);
    const [invitationCode, setInvitationCode] = useState('');
    const [validating, setValidating] = useState(false);
    const [codeValid, setCodeValid] = useState(false);
    const [invitedRoleCode, setInvitedRoleCode] = useState<string | undefined>();

    // 验证邀请码
    const handleValidateCode = async () => {
        const code = invitationCode.trim();
        if (!code) {
            messageApi.error(t('auth.register.codeEmpty'));
            return;
        }

        setValidating(true);
        try {
            const result = await invitationCodeService.validate(code);
            if (result.valid) {
                messageApi.success(result.message);
                setCodeValid(true);
                setInvitedRoleCode(result.roleCode);
                setCurrentStep(1);
            } else {
                messageApi.error(result.message);
                setCodeValid(false);
                setInvitedRoleCode(undefined);
            }
        } catch (error: any) {
            messageApi.error(t('auth.register.codeFailed'));
            setCodeValid(false);
            setInvitedRoleCode(undefined);
        } finally {
            setValidating(false);
        }
    };

    // 返回上一步
    const handlePrevStep = () => {
        setCurrentStep(0);
    };

    // 提交注册
    const handleSubmit = async (values: RegisterFormValues) => {
        const success = await register(values.username, values.password, values.email, invitationCode);
        if (success) {
            navigate(buildLocalizedSitePath('/login', locale));
        }
    };

    // 密码确认验证
    const validateConfirmPassword = (_rule: any, value: string) => {
        if (value && value !== form.getFieldValue('password')) {
            return Promise.reject(new Error(t('auth.register.confirmMismatch')));
        }
        return Promise.resolve();
    };

    // 步骤配置
    const steps = [
        {
            title: t('auth.register.step.verify'),
            description: t('auth.register.step.verifyDescription'),
        },
        {
            title: t('auth.register.step.create'),
            description: t('auth.register.step.createDescription'),
        },
    ];

    return (
        <div className={styles.shell} style={{
            gridTemplateColumns: '1fr',
            justifyItems: 'center',
            justifyContent: 'center'
        }}>
            <div className={styles.langSwitch}>
                <LanguageSwitcher className={styles.switcher}/>
            </div>
            <div className={styles.shellContent}>
                <Card
                    className={styles.card}
                    style={{width: 'min(100%, 480px)', margin: 0}}
                    styles={{
                        body: {padding: 40}
                    }}
                >
                    <div className={styles.header}>
                        <img alt="D-Sense" className={styles.brandLogo} src="/d-sense.png"/>
                        <h2>{t('auth.register.cardTitle')}</h2>
                        <p>{t('auth.register.cardDescription')}</p>
                    </div>

                    <Steps
                        current={currentStep}
                        items={steps}
                        size="small"
                        style={{marginBottom: 32}}
                    />

                    {/* 步骤1: 验证邀请码 */}
                    {currentStep === 0 && (
                        <div>
                            <div style={{marginBottom: 24, textAlign: 'center'}}>
                                <GiftOutlined style={{fontSize: 48, color: '#F0B429', marginBottom: 16}}/>
                                <div>
                                    <Text style={{color: '#8892A4'}}>{t('auth.register.enterCode')}</Text>
                                </div>
                            </div>
                            <Form layout="vertical" requiredMark={false}>
                                <Form.Item
                                    label={t('auth.register.codeLabel')}
                                    required
                                >
                                    <Input
                                        prefix={<GiftOutlined style={{color: '#8892A4'}}/>}
                                        placeholder={t('auth.register.codePlaceholder')}
                                        value={invitationCode}
                                        onChange={(e) => {
                                            setInvitationCode(e.target.value);
                                            setCodeValid(false);
                                            setInvitedRoleCode(undefined);
                                        }}
                                        onPressEnter={handleValidateCode}
                                        size="large"
                                        status={invitationCode && !codeValid ? undefined : undefined}
                                        suffix={codeValid ? <CheckCircleOutlined style={{color: '#52c41a'}}/> : null}
                                    />
                                </Form.Item>
                                <Form.Item style={{marginBottom: 16}}>
                                    <Button
                                        type="primary"
                                        block
                                        onClick={handleValidateCode}
                                        loading={validating}
                                        size="large"
                                    >
                                        {t('auth.register.codeSubmit')}
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>
                    )}

                    {/* 步骤2: 填写用户信息 */}
                    {currentStep === 1 && (
                        <Form
                            form={form}
                            name="register"
                            onFinish={handleSubmit}
                            layout="vertical"
                            requiredMark={false}
                            size="large"
                        >
                            <div className={styles.inviteNote}>
                                <Text style={{color: '#F0B429', fontSize: 13}}>
                                    <GiftOutlined style={{marginRight: 6}}/>
                                    {t('auth.register.currentCode')}: {invitationCode}
                                </Text>
                                {invitedRoleCode && (
                                    <Text style={{color: '#8892A4', fontSize: 12, marginLeft: 16}}>
                                        {invitedRoleCode}
                                    </Text>
                                )}
                            </div>

                            <Form.Item
                                name="username"
                                rules={[
                                    {required: true, message: t('auth.register.usernameRequired')},
                                    {min: 3, message: t('auth.register.usernameMin')},
                                    {max: 50, message: t('auth.register.usernameMax')},
                                ]}
                            >
                                <Input
                                    prefix={<UserOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.register.usernamePlaceholder')}
                                    autoComplete="username"
                                />
                            </Form.Item>

                            <Form.Item
                                name="email"
                                rules={[
                                    {required: true, message: t('auth.register.emailRequired')},
                                    {type: 'email', message: t('auth.register.emailInvalid')},
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.register.emailPlaceholder')}
                                    autoComplete="email"
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                rules={[
                                    {required: true, message: t('auth.register.passwordRequired')},
                                    {min: 6, message: t('auth.register.passwordMin')},
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.register.passwordPlaceholder')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                rules={[
                                    {required: true, message: t('auth.register.confirmRequired')},
                                    {validator: validateConfirmPassword},
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined style={{color: '#8892A4'}}/>}
                                    placeholder={t('auth.register.confirmPlaceholder')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Form.Item style={{marginBottom: 16}}>
                                <div className={styles.dualActions}>
                                    <Button onClick={handlePrevStep} style={{flex: 1}}>
                                        {t('auth.register.previous')}
                                    </Button>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        loading={isLoading}
                                        style={{flex: 2}}
                                    >
                                        {t('auth.register.submit')}
                                    </Button>
                                </div>
                            </Form.Item>
                        </Form>
                    )}

                    <div className={styles.footer}>
                        <Text className={styles.footerText}>
                            {t('auth.register.existing')}{' '}
                            <Link to={buildLocalizedSitePath('/login', locale)} className={styles.footerLink}>
                                {t('auth.register.login')}
                            </Link>
                        </Text>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default RegisterPage;